'use client'
import React, { useCallback, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import type { FormFieldBlock, Form as FormType } from '@payloadcms/plugin-form-builder/types'
import { useRouter } from 'next/navigation'

import type { LeadgenformBlock as LeadgenformBlockProps } from '@/payload-types'

import RichText from '@/components/RichText'
import { Button } from '@/components/ui/button'
import { cn } from '@/utilities/ui'
import { ChevronsRight } from 'lucide-react'
import { fields } from '@/blocks/Form/fields'
import { getClientSideURL } from '@/utilities/getURL'
import { prepareSubmissionData } from '@/utilities/formSubmission'

const LeadgenformBlock: React.FC<LeadgenformBlockProps> = ({
  title,
  form: formFromProps,
  submitButtonLabel,
}) => {
  // Extract form object from relationship
  const form = typeof formFromProps === 'object' && formFromProps !== null ? formFromProps : null

  const formMethods = useForm({
    defaultValues: form?.fields as any,
  })

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = formMethods

  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>()
  const [error, setError] = useState<{ message: string; status?: string } | undefined>()
  const router = useRouter()

  const { id: formID, confirmationMessage, confirmationType, redirect } = form || {}

  const onSubmit = useCallback(
    (data: FormFieldBlock[]) => {
      let loadingTimerID: ReturnType<typeof setTimeout>
      const submitForm = async () => {
        setError(undefined)

        const dataToSend = prepareSubmissionData(data as unknown as Record<string, unknown>)

        loadingTimerID = setTimeout(() => {
          setIsLoading(true)
        }, 1000)

        try {
          const req = await fetch(`${getClientSideURL()}/api/form-submissions`, {
            body: JSON.stringify({
              form: formID,
              submissionData: dataToSend,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'POST',
          })

          const res = await req.json()
          clearTimeout(loadingTimerID)

          if (req.status >= 400) {
            setIsLoading(false)
            setError({
              message: res.errors?.[0]?.message || 'Internal Server Error',
              status: res.status,
            })
            return
          }

          setIsLoading(false)
          setHasSubmitted(true)

          if (confirmationType === 'redirect' && redirect) {
            const { url } = redirect
            const redirectUrl = url
            if (redirectUrl) router.push(redirectUrl)
          }
        } catch (err) {
          console.warn(err)
          setIsLoading(false)
          setError({
            message: 'Something went wrong.',
          })
        }
      }

      void submitForm()
    },
    [router, formID, redirect, confirmationType],
  )

  // Render all fields from the attached form
  const filteredFields = form?.fields || []

  if (!form) {
    return (
      <section
        className={cn('relative bg-background')}
        aria-label="Lead Generation Form"
        style={{ backgroundColor: 'var(--primary)' }}
      >
        <div className="container mx-auto text-center">
          <p className="text-primary-foreground">No form selected</p>
        </div>
      </section>
    )
  }

  return (
    <section
      className={cn('relative bg-background')}
      aria-label="Lead Generation Form"
      style={{ backgroundColor: 'var(--primary)' }}
    >
      <div className={cn('container mx-auto 2xl:px-0 relative z-10 py-8')}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 xl:gap-4 items-center">
          {/* Left Side - Title */}
          <div className="lg:col-span-1">
            {/* Title */}
            <div className="text-center lg:text-left">
              {title && (
                <div className="text-2xl lg:text-3xl font-bold text-primary-foreground leading-tight">
                  <RichText
                    data={title}
                    enableGutter={false}
                    enableProse={false}
                    style={{ fontFamily: 'var(--font-sans)' }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Center - Form Fields and Submit Button */}
          <div className="lg:col-span-2 w-full flex justify-center">
            <div className="w-full max-w-3xl bg-background/90 rounded-3xl px-6 py-8 sm:px-8 sm:py-10">
              <FormProvider {...formMethods}>
                {!isLoading &&
                  hasSubmitted &&
                  confirmationType === 'message' &&
                  confirmationMessage && (
                    <div className="text-center mb-6">
                      <div className="text-primary-foreground">
                        <RichText data={confirmationMessage} />
                      </div>
                    </div>
                  )}
                {error && (
                  <div className="text-red-300 font-bold text-center mb-6">
                    {`${error.status || '500'}: ${error.message || ''}`}
                  </div>
                )}
                {!hasSubmitted && (
                  <form id={formID} onSubmit={handleSubmit(onSubmit)} className="w-full">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      {/* Form Fields */}
                      {filteredFields.map((field: any, index: number) => {
                        const Field: React.FC<any> =
                          fields?.[field.blockType as keyof typeof fields]

                        if (!Field) return null

                        const isFullWidthField =
                          field.blockType === 'textarea' || field.blockType === 'message'

                        return (
                          <div
                            key={index}
                            className={cn(
                              'w-full [&_label]:text-primary-foreground [&_.text-gray-700]:text-primary-foreground',
                              isFullWidthField ? 'md:col-span-2' : 'md:col-span-1',
                            )}
                          >
                            <Field
                              form={form}
                              {...field}
                              {...formMethods}
                              control={control}
                              errors={errors}
                              register={register}
                            />
                          </div>
                        )
                      })}
                    </div>

                    {/* Submit Button */}
                    <div className="mt-6 flex flex-col items-center gap-4">
                      <Button
                        form={formID}
                        type="submit"
                        disabled={isLoading}
                        className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 rounded-full px-10 py-4 text-base font-bold transition-all duration-300 h-auto whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <span className="inline-flex items-center justify-center gap-2">
                          {submitButtonLabel || 'Submit Request'}
                          <span className="text-primary font-bold text-lg">»</span>
                        </span>
                      </Button>
                    </div>

                    {/* Loading Message */}
                    {isLoading && !hasSubmitted && (
                      <div className="mt-4 flex items-center justify-center">
                        <p className="text-lg text-primary-foreground">Loading, please wait...</p>
                      </div>
                    )}
                  </form>
                )}
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default LeadgenformBlock
